import { useEffect, useRef } from 'react';
import * as Comlink from 'comlink';
import { usePathname } from 'next/navigation';
import { useIsUserIdentified } from '@frameio/segment/contexts';
import { processQueue as processSegmentEventQueue } from '@frameio/segment-ot';
import { useActiveShareId } from '@frameio/shared/contexts/ActiveEntitiesContext/ActiveShareIdContext';
import { getPageType } from '@frameio/shared/routing/getPageType';
import { useTrack } from '@frameio/shared/segment/hooks';
import {
  emitEvent,
  reportError,
  WORKER_EMIT_EVENT as TELEMETRY_WORKER_EVENT,
  WORKER_ERROR_EVENT as TELEMETRY_WORKER_ERROR,
} from '@frameio/telemetry';
import { auth, isServer, shareAuth } from '@frameio/utils';
import type { AuthState } from '@frameio/utils/auth/auth';
import { isUXPEnvironment } from '@frameio/utils/config';
import { formatFileSizeInMB } from '@frameio/utils/formatters';
import { session } from '@frameio/utils/session';
import { UploaderConfigs } from '../constants';
import type { TransferBatch, UploadJobEvent } from '../types/uploader';
import type { UploaderWorker } from '../UploaderWorker';
import { useUploaderTelemetry } from './useUploaderTelemetry';

// Set by negative's build to the worker it bundled separately.
declare const UPLOAD_WORKER_URL: string | undefined;

type EventName = string;
type ListenerId = string;
type ListenerValue = (arg?: any) => void;
type EventListenersMap = Map<ListenerId, ListenerValue>;

let uploaderWorker: UploaderWorker;
// Stores tracked listeners in a map key: eventName with a map by listener Ids.
const trackedListeners = new Map<EventName, EventListenersMap>();

export function useUploaderWorker() {
  const activeShareId = useActiveShareId();
  const { sendToTelemetry } = useUploaderTelemetry();
  const track = useTrack();
  const userIsIdentified = useIsUserIdentified();

  // Only set the shareId for shareReviewReel and shareReviewFinder pages
  const pathname = usePathname();
  const pageType = getPageType(pathname);
  const shareId = ['shareReviewReel', 'shareReviewFinder'].includes(
    pageType ?? ''
  )
    ? activeShareId
    : undefined;
  const passphrase = shareId ? shareAuth.getPassphraseById(shareId) : undefined;

  // We are using a ref here since the uploader worker needs to know when
  // event tracking can start. It otherwise can't keep track of state changes
  // since it's mounted once and never unmounted.
  const canTrackEvents = useRef<boolean>(userIsIdentified.isUserIdentified);

  useEffect(() => {
    if (userIsIdentified.isUserIdentified) {
      canTrackEvents.current = true;
      processSegmentEventQueue();
    }
  }, [userIsIdentified]);

  if (!isServer && !uploaderWorker) {
    const worker =
      typeof UPLOAD_WORKER_URL !== 'undefined'
        ? new Worker(UPLOAD_WORKER_URL, { type: 'module' })
        : new Worker(
            new URL('../UploaderWorker/Uploader.worker.ts', import.meta.url)
          );

    worker.addEventListener('message', (ev) => {
      const eventType = ev?.data?.eventType;
      const payload = ev?.data?.payload;
      const isJobEvent = eventType && eventType.startsWith('job');
      if (eventType) {
        const eventListeners = trackedListeners.has(eventType)
          ? trackedListeners.get(eventType)
          : [];
        Array.from(eventListeners?.values() || []).forEach((listener) => {
          try {
            listener(payload[0]);
          } catch (err) {
            reportError(err);
          }
        });

        try {
          if (isJobEvent) sendToTelemetry(ev);
        } catch (e) {
          reportError(e);
        }
      }

      if (isJobEvent) {
        const { job }: { job: TransferBatch } = payload[0] || undefined;
        if (eventType === 'jobTransferStarted') {
          track('upload-started', {
            item_count: job.fileCounts?.total,
            folder_count: job.numOfCreatedFolders,
            transfer_job_id: job.id,
            total_file_size: formatFileSizeInMB(job.progress?.bytesTotal ?? 0),
          });
        } else if (eventType === 'jobCompleted') {
          track('upload-completed', {
            item_count: job.fileCounts?.total,
            folder_count: job.numOfCreatedFolders,
            transfer_job_id: job.id,
            total_file_size: formatFileSizeInMB(job.progress?.bytesTotal ?? 0),
            completed_item_count: job.fileCounts?.uploaded,
            failed_item_count: job.fileCounts?.failed,
          });
        } else if (eventType === 'jobCanceled') {
          // Canceled jobs can happen before an ID is created, but we only want to track when an ID is present on the server.
          // LocalId is a temporary ID that is created before the server ID is created.
          if (job.id) {
            track('upload-canceled', {
              item_count: job.fileCounts?.total,
              transfer_job_id: job.id,
              total_file_size: formatFileSizeInMB(
                job.progress?.bytesTotal ?? 0
              ),
              completed_item_count: job.fileCounts?.uploaded,
            });
          }
        } else if (eventType === 'jobPaused') {
          track('upload-paused', {
            item_count: job.fileCounts?.total,
            transfer_job_id: job.id,
            total_file_size: formatFileSizeInMB(job.progress?.bytesTotal ?? 0),
            completed_item_count: job.fileCounts?.uploaded,
          });
        } else if (eventType === 'jobResumed') {
          track('upload-resumed', {
            item_count: job.fileCounts?.total,
            transfer_job_id: job.id,
            total_file_size: formatFileSizeInMB(job.progress?.bytesTotal ?? 0),
          });
        } else if (eventType === 'jobFailed') {
          track('upload-failed', {
            item_count: job.fileCounts?.total,
            transfer_job_id: job.id,
            total_file_size: formatFileSizeInMB(job.progress?.bytesTotal ?? 0),
          });
        } else {
          return;
        }
        // Call to process segment event queue that accumulate after the
        // above tracking methods are called from the worker.
        if (canTrackEvents.current) {
          processSegmentEventQueue();
        }
      } else if (eventType === 'authRefresh') {
        auth
          .refresh(payload.hash, {
            signal: AbortSignal.timeout(10_000),
          })
          .then((state: AuthState) => {
            uploaderWorker.setAuthState(state);
          })
          // eslint-disable-next-line no-console
          .catch(console.error);
      } else if (eventType === TELEMETRY_WORKER_ERROR) {
        reportError(JSON.parse(payload));
      } else if (eventType === TELEMETRY_WORKER_EVENT) {
        const { name, context, opts } = payload;
        emitEvent(name, context, opts);
      }
    });

    uploaderWorker = Comlink.wrap(worker);

    const allTokens = auth.getTokens({
      share: passphrase ? `${shareId}:${passphrase}` : shareId,
    });

    if (
      allTokens.refresh ||
      allTokens.session ||
      allTokens.share ||
      isUXPEnvironment()
    ) {
      uploaderWorker.configure({
        tokens: allTokens,
        sessionId: session.getSessionId(),
        concurrency: UploaderConfigs.S3_QUEUE_CONCURRENCY_SIZE.value,
        uploadjobOptions: {
          shouldVerifyChecksum: UploaderConfigs.VERIFY_CHECKSUM.value === 1,
          batchSize: UploaderConfigs.UPLOAD_JOB_BATCH_SIZE.value,
          timings: {
            marginOfErrorSeconds:
              UploaderConfigs.UPLOAD_JOB_TIMIINGS_MARGIN_OF_ERROR_SECONDS.value,
            uploadSpeedMeasureSeconds:
              UploaderConfigs.UPLOAD_JOB_TIMIINGS_UPLOAD_SPEED_MEASURE_SECONDS
                .value,
            reportIntervalMilli:
              UploaderConfigs.UPLOAD_JOB_TIMIINGS_REPORT_INTERVAL_MILLISECONDS
                .value,
          },
        },
      });
    }

    auth.subscribe((state) => {
      uploaderWorker.setAuthState(state);
    });
  }

  return {
    worker: uploaderWorker,
    on: (eventName: UploadJobEvent, listener: ListenerValue) => {
      const id = crypto.randomUUID();
      const eventListeners: EventListenersMap = trackedListeners.has(eventName)
        ? trackedListeners.get(eventName)!
        : new Map();
      eventListeners.set(id, listener);
      trackedListeners.set(eventName, eventListeners);
      return id;
    },
    off: (eventName: UploadJobEvent, listenerId: ListenerId) => {
      const eventListeners = trackedListeners.has(eventName)
        ? trackedListeners.get(eventName)
        : null;

      if (!eventListeners) return;

      eventListeners.delete(listenerId);
      trackedListeners.set(eventName, eventListeners);
    },
  };
}
